import React from 'react';
import PropTypes from 'prop-types';
import PreferencesSection from '../PreferencesSection';
import css from '../PreferencesSection.scss';
import Checkbox from 'SF/Checkbox';
import { StaffNotificationPreferences } from 'utils/types';
import aptCss from './AppointmentPreferences.scss';
import HourlyDropdown from 'common/HourlyDropdown';
import { alertMinutesBeforeOptions, reminderDayOptions } from '../selectionOptions';
import Select from 'common/StyledSelect';
import { find } from 'lodash';
import { ifFieldEmptyReturnDefault } from 'utils/text';

const AppointmentPreferences = ({ notificationPreferences, setValue, weekendReminderEmailSettingEnabled }) => {
    const getWeekendReminderCheck = () => {
        if (!weekendReminderEmailSettingEnabled) return null;
        return (
            <div className={aptCss['checkbox-option']}>
                <Checkbox ariaLabel=""
                          aria-labelledby="weekendReminderLabel"
                          checked={notificationPreferences.weekendRemindersOff}
                          onChange={(checked) => setValue({ weekendRemindersOff: checked })} />
                <div className={css['profile-adv-options']}>
                    <label id="weekendReminderLabel">
                        I <span className={css['bold']}>do not</span> want to receive appointment notifications during the weekend.
                        <br/>
                        <span className={css['profile-sub-text']}>
                            When selected, appointment notifications for Saturday, Sunday, or Monday will be sent on the Friday prior to those appointments.
                        </span>
                    </label>
                </div>
            </div>
        )
    };
    
    const getDigestOptions = () => (
        <div className={aptCss['digest-options']}>
            <label>Send at</label>
            <br/>
            <HourlyDropdown id="digestTime"
                            ariaLabel="Send at time"
                            className={aptCss['dropdown'] + ' ' + aptCss['hourly-dropdown']}
                            isDisabled={notificationPreferences.planningReminderType === 'DONT_SEND'}
                            onChange={(hour) => setValue({ planningReminderTime: hour })}
                            value={notificationPreferences.planningReminderTime} />
            <Select id="digestDay"
                    ariaLabel="Send on day"
                    className={aptCss['dropdown'] + ' ' + aptCss['daily-dropdown']}
                    isDisabled={notificationPreferences.planningReminderType === 'DONT_SEND'}
                    onChange={day => setValue({ planningReminderWhen: day.value })}
                    options={reminderDayOptions}
                    value={find(reminderDayOptions, ['value', notificationPreferences.planningReminderWhen])} />
        </div>
    );
    
    return (
        <PreferencesSection isStaff
                            className={aptCss['appointment-options']}
                            headerText="Appointments"
                            descriptionText="Customize appointment notification preferences.">
            {getWeekendReminderCheck()}
            <h4>Planning Reminders</h4>
            <p id="planningReminderLabel">Choose how you want to receive reminders about your appointments.</p>
            <div data-role="controlgroup" aria-labelledby="planningReminderLabel">
                <div className={aptCss['no-reminders']}>
                    <input type="radio"
                           aria-labelledby="noReminders"
                           name="planningReminders"
                           value="DONT_SEND"
                           onChange={() => setValue({ planningReminderType: 'DONT_SEND' })}
                           checked={notificationPreferences.planningReminderType === 'DONT_SEND'} />
                    <span id="noReminders">Don&apos;t send me planning reminders</span>
                </div>
                <div className={aptCss['separate-reminders']}>
                    <input type="radio"
                           aria-labelledby="separateReminders"
                           name="planningReminders"
                           value="SEPARATE"
                           onChange={() => setValue({ planningReminderType: 'SEPARATE' })}
                           checked={notificationPreferences.planningReminderType === 'SEPARATE'} />
                    <div>
                        <span id="separateReminders">Send me a notification individually for each appointment</span>
                        {notificationPreferences.planningReminderType === 'SEPARATE' && getDigestOptions()}
                    </div>
                </div>
                <div className={aptCss['digest-reminders']}>
                    <input type="radio"
                           aria-labelledby="digestReminders"
                           name="planningReminders"
                           value="ONE"
                           onChange={() => setValue({ planningReminderType: 'ONE' })}
                           checked={notificationPreferences.planningReminderType === 'ONE'} />
                    <div>
                        <span id="digestReminders">Send me a digest of all appointments</span>
                        {notificationPreferences.planningReminderType === 'ONE' && getDigestOptions()}
                    </div>
                </div>
            </div>
            <h4>Alert Reminders</h4>
            <p>Specify whether you want an alert just before the appointment starts.</p>
            <div className={aptCss['checkbox-option']}>
                <Checkbox ariaLabel=""
                          aria-labelledby="alertReminderLabel"
                          checked={notificationPreferences.appAlertFlag}
                          data-testid="alertReminder-checkbox"
                          onChange={(checked) => setValue({ appAlertFlag: checked })} />
                <div className={css['profile-adv-options']}>
                    <label id="alertReminderLabel">
                        Notify me <span className={css['bold'] + ' ' + css['underline']}>before the start</span>:
                    </label>
                    <br/>
                    <Select id="alertReminder"    
                            ariaLabel="Notify me before the start of the appointment (minutes)"
                            className={aptCss['dropdown']}
                            isDisabled={!notificationPreferences.appAlertFlag}
                            onChange={minutes => setValue({ alertPreviousTime: minutes.value })}
                            options={alertMinutesBeforeOptions}
                            value={ifFieldEmptyReturnDefault(find(alertMinutesBeforeOptions, ['value', notificationPreferences.alertPreviousTime]),
                                find(alertMinutesBeforeOptions, ['value', 15]))}/>
                </div>
            </div>
        </PreferencesSection>
    )
};

AppointmentPreferences.propTypes = {
    notificationPreferences: StaffNotificationPreferences,
    setValue: PropTypes.func,
    weekendReminderEmailSettingEnabled: PropTypes.bool
};

export default AppointmentPreferences;
